import { ReactElement } from "react";
import { UseControllerReturn } from "react-hook-form";

import { CustomFieldType, FieldType } from "Views/Bills/V2/components/FormGenerator/type";
import { PreviewField } from "Views/Bills/V2/components/Preview/type";

export type VendorDetailData = {
  dynamicFields: {
    id: number;
    alias: string;
    value?: string;
    orderNumber: number;
  }[];
  legalName: string;
  organisationID: string;
};

export interface BillFormProviderProps {
  vendorDetail?: VendorDetailData;
  setVendorDetail(vendorData: VendorDetailData): void;
  handleSaveDraft(): void;
  // to handle refetch FieldGenerator
  refetchValues?: {
    vendorID?: string;
    isAddNotes?: boolean;
  };
  isSubmitLoading?: boolean;
  setIsSubmitLoading?(value: boolean): void;
  isFxRateExpired?: boolean;
  setIsFxRateExpired?(value: boolean): void;
}

export enum BillFlowEnum { // TO DO: add Edit Draft and Edit Bill
  NEW_BILL = "New Bill",
  EDIT_BILL = "Edit Bill",
  EDIT_DRAFT = "Edit Draft",
}

export interface BillFormStepProps {
  onBack(): void;
  onNext(): void;
  billFlow: BillFlowEnum;
}

export interface BillDetailFormProps extends BillFormStepProps {
  isShowForm: boolean;
  onShowForm(): void;
}

export interface FormFooterProps {
  onClickBack(): void;
  onSubmit(): void;
  submitText: string;
  isSubmitLoading?: boolean;
  disableSubmit?: boolean;
}

export interface FieldConsumerType {
  isOnChangeRefetch?: boolean;
  resetFields?: string[]; // name of the fields that wanted to be reset
}

export interface BasicFieldGenerator {
  label?: string;
  name: string;
  defaultValue?: unknown;
  tooltip?: string;
  rules?: {
    [key: string]: any;
  };
  showError?: boolean;
  fieldProps?: {
    fieldType: FieldType | CustomFieldType;
    [key: string]: any;
  };
  withOCR?: boolean;
  isSameWithOCR?: boolean;
}
export interface FieldGroupGenerator extends BasicFieldGenerator {
  fieldGroup: BasicFieldGenerator[];
}

export type FieldGenerator = BasicFieldGenerator | FieldGroupGenerator;

export type BillFieldGenerator =
  | (BasicFieldGenerator & {
      className?: string;
      render?(props: UseControllerReturn): ReactElement;
    })
  | (Omit<FieldGroupGenerator, "fieldGroup"> & {
      render?(props: UseControllerReturn): ReactElement; // field render
      fieldGroup: (BasicFieldGenerator & {
        className?: string;
        render?(props: UseControllerReturn): ReactElement; // sub field render
      })[];
    });

export enum BillFieldNames {
  invoiceAmount = "billTax.invoiceAmount",
  invoiceCurrency = "billTax.invoiceCurrency",
}

export type BillFormFields = {
  vendorID: Number;
  billNumber: string; // invoice number
  issuedDate: string;
  dueDate: string;
  expenseCategoryID: string;
  taxID: string;
  tags: string; // this should be string[]
  notes: string;
  amount: Number;
  currency: string;
  youPayCurrency: string;
  totalPaidAmount: Number;
  paymentScheduleTime: string;
  paymentPurpose: string;
  receiptEmailRecipients: string; // this should be string[]
  additionalAttachments: File[];
  source: "portal_single" | "portal_drag_and_drop" | "portal_xero";
  ocrID: string;
  deletedAttachmentIDs: string[];
  uploadedAttachments: {
    id: string;
    filename: string;
    size: number;
    url: string;
  }[];
  billTax: {
    invoiceAmount: Number;
    invoiceCurrency: string;
    taxRate: Number;
    vatRate: Number;
    vatAmount: Number;
    whtAmount: Number;
    hasManualWHT: boolean;
  };
  walletID: string;
  isAddNotes: boolean; // might need to remove this
};
export interface OCRExtractData {
  ocrID: string;
  vendorID?: number;
  billNumber?: string;
  issuedDate?: string;
  dueDate?: string;
  isSameWithOCR: boolean;
  amount?: number;
  recipientDetail?: Record<string, any>;
}

export interface OCRData {
  billData?: Omit<OCRExtractData, "recipientDetail">;
  recipientData?: Record<string, any>;
}

export interface RecipientData {
  bankAccountName: string;
  bankAccountNumber: string;
  bankName: string;
  name: string;
}

export interface BillDetailData {
  billNumber: string;
  dueDate: string;
  expenseCategoryID: string;
  invoiceAmount: number;
  invoiceCurrency: string;
  issuedDate: string;
  notes: string;
  tags?: any;
  taxID: string;
  recipient: RecipientData;
}

export interface PaymentDetailData {
  amount: number;
  beneficiaryRemarks: string;
  currency: string;
  estimatedArrival: string;
  walletCurrency: string;
  isFXRateChanged: boolean;
  paymentScheduleTime: string;
  sendPaymentUpdatesTo: null | string[];
  totalPaidAmount: number;
}

export interface BillPreviewDataResponse {
  billDetail: BillDetailData;
  paymentDetail: PaymentDetailData;
  sections: PreviewField[];
  isEdited: boolean;
}

export interface SaaSConfig {
  fields: Array<{ field: string; label: string }>;
  filterStatuses: Array<{ id: string; label: string }>;
  filterSorts: Array<{ columnID: string; columnLabel: string; orderID: string; orderLabel: string }>;
  isPaymentEnabled: boolean;
  isPaymentRun: boolean;
}
